/**
 * T17 + T20 — turn-order rail model. The top roster banner renders players in
 * `turnOrder` (round start player first). Few-player (≤5) games draw twice per
 * round, so each player appears once per pass (1패스 | 2패스). Pure + testable.
 * T20: each slot carries its turn STATE (passed / current / future) so the banner
 * renders enabled/disabled dots instead of a footprint-inflating current ring.
 */

export interface RailSlot {
  readonly playerIndex: number;
  /** 1 or 2 (few-player games run a 2nd pass). */
  readonly pass: number;
  /** The round's start player (turnOrder[0], pass 1). Drives the round-change pulse. */
  readonly isStart: boolean;
  /** The active turn right now (matching pass + position). */
  readonly isCurrent: boolean;
  /**
   * A turn that has already acted this round — an earlier pass, or the same pass
   * at an earlier position. Rendered disabled (T20); `!isCurrent && !isPassed`
   * means the turn is still to come (future).
   */
  readonly isPassed: boolean;
  /** The player who used 토큰 포기 this round, in the pass it happened — X marker. */
  readonly isForfeit: boolean;
  /** Stable React key (player + pass). */
  readonly key: string;
}

interface BuildRosterRailParams {
  readonly turnOrder: readonly number[];
  readonly currentTurnIndex: number;
  readonly drawPass: number;
  readonly forfeitPlayerIndex: number | null;
  /** isFewPlayerVariant(playerCount) — ≤5 players ⇒ doubled rail. */
  readonly fewPlayer: boolean;
}

export function buildRosterRail({
  turnOrder,
  currentTurnIndex,
  drawPass,
  forfeitPlayerIndex,
  fewPlayer,
}: BuildRosterRailParams): readonly RailSlot[] {
  const passes = fewPlayer ? 2 : 1;
  const slots: RailSlot[] = [];
  for (let pass = 1; pass <= passes; pass += 1) {
    for (let pos = 0; pos < turnOrder.length; pos += 1) {
      const playerIndex = turnOrder[pos] ?? 0;
      slots.push({
        playerIndex,
        pass,
        isStart: pass === 1 && pos === 0,
        isCurrent: pass === drawPass && pos === currentTurnIndex,
        isPassed:
          pass < drawPass || (pass === drawPass && pos < currentTurnIndex),
        isForfeit:
          forfeitPlayerIndex !== null &&
          playerIndex === forfeitPlayerIndex &&
          pass === drawPass,
        key: `p${pass}-${playerIndex}`,
      });
    }
  }
  return slots;
}
